/**
 * タグの一括登録の差分計算（FKV3-13002）
 *
 * タグ種別・タグ名・付与・店舗一覧はすべて画面が取得済みなので、差分はここで完結する。
 * サーバに問い合わせないのは、往復を減らすためと、類似判定のロジックを
 * フロント・バックエンドで二重に持たないため。
 */

import Papa from 'papaparse';
import { checkTagName, normalizeDisplayName, strictKey } from './tagNormalize';

/** 登録対象の店舗（マスタ側） */
export interface ImportSeller {
  /** 店舗ID（内部ID。`sellers.id`）。サーバへ送る付与キー */
  sellerId: string;
  /** 店舗ID（顧客側）。CSVに書くのはこちら */
  sellerCustomerId: string;
  sellerName: string;
  hierarchy: string;
}

/** 既存のタグ名 */
export interface ImportTag {
  id: string;
  name: string;
}

/** CSV から読んだ1行 */
export interface ParsedRow {
  /** 1始まり。ヘッダ行を含めた行番号（画面に出して該当行を探せるようにする） */
  lineNumber: number;
  sellerCustomerId: string;
  tagName: string;
}

/** 新しく作られるタグ名 */
export interface NewTagName {
  name: string;
  /** 似ている既存のタグ名。あれば「まとめる」候補として見せる */
  similarTo?: string;
}

/** 同じ店舗に複数のタグ名が指定されている */
export interface DuplicateSeller {
  sellerCustomerId: string;
  sellerName: string;
  /** 指定された行。後の行を採用するのが既定 */
  rows: ParsedRow[];
}

/** 登録で何が起きるか */
export interface ImportPlan {
  /** 新しく作られるタグ名 */
  newTagNames: NewTagName[];
  /** 同じ店舗が複数行にある（1店舗1タグ名なのでどちらを採るか決められない） */
  duplicateSellers: DuplicateSeller[];
  /** マスタに存在しない店舗ID */
  unknownSellerIds: { lineNumber: number; sellerCustomerId: string }[];
  /** タグが付いていない状態から新しく付く件数 */
  newlyAssigned: number;
  /** すでに別のタグ名が付いていて置き換わる件数 */
  replaced: number;
  /** すでに同じタグ名が付いていて変わらない件数 */
  unchanged: number;
  /** 洗い替えを選んだときに外れる店舗数（CSVに無い店舗） */
  unassignedIfReplace: number;
  /** 実際に適用する内容（店舗の内部IDとタグ名。タグ名IDは作成後に解決する） */
  entries: { sellerId: string; tagName: string }[];
}

/** 同じ店舗が複数行あったときにどの行を採るか */
export type DuplicateResolution = 'last' | 'first' | 'skip';

export interface BuildImportPlanInput {
  rows: ParsedRow[];
  /** マスタの店舗一覧 */
  sellers: ImportSeller[];
  /** 選択中のタグ種別に既にあるタグ名 */
  existingTags: ImportTag[];
  /** 選択中のタグ種別の現在の付与（店舗の内部ID → タグ名ID） */
  currentAssignments: Map<string, string>;
  /** 同じ店舗が複数行あったときの扱い。既定は後の行を採用 */
  duplicateResolution?: DuplicateResolution;
  /** 新しいタグ名を作らず、既存のタグ名だけ登録する */
  skipNewTagNames?: boolean;
  /** 新規になるタグ名を既存へ読み替える（表記揺れを吸収する最後の砦） */
  mergeInto?: Map<string, string>;
}

/**
 * 貼り付けテキスト・CSV を行に変換する。
 *
 * 入力形式は雛形CSVと同じ `店舗ID, 階層, 店舗名, タグ名`。雛形に書き込んだものを
 * 列を削らずそのまま貼り戻せることを優先している。階層と店舗名は目視確認のための
 * 列で、登録には使わない（マスタ側と食い違っていても無視する）。
 */
export function parseImportText(text: string): { rows: ParsedRow[]; error?: string } {
  const parsed = Papa.parse<string[]>(text.trim(), {
    skipEmptyLines: 'greedy',
    // 区切り文字（カンマ/タブ）は papaparse に判定させる
  });
  if (parsed.data.length === 0) {
    return { rows: [], error: '登録する行がありません' };
  }

  const table = parsed.data.map((row) => row.map((cell) => (cell ?? '').trim()));

  // ヘッダ行の判定。列名が分かればその位置を使い、分からなければ列数で決める
  const header = table[0];
  const headerIndex = findColumnIndexes(header);
  // 店舗ID列があるのにタグ名列が無いのは、固定列しか無いワイド形式（タグ種別0件の店舗階層一覧CSV等）。
  // 列数で位置を推し量ると、4列目の郵便番号をタグ名として読んでしまうため止める
  if (!headerIndex && header.some((h) => h === '店舗ID' || h === '店舗コード')) {
    return { rows: [], error: 'タグ種別の列が見つかりません（固定列しかありません）' };
  }
  const body = headerIndex ? table.slice(1) : table;
  const index = headerIndex ?? fallbackColumnIndexes(header.length);
  if (!index) {
    return {
      rows: [],
      error:
        '列を判別できません。雛形CSV（店舗ID, 階層, 店舗名, タグ名）の形式で貼り付けてください',
    };
  }

  const rows: ParsedRow[] = [];
  body.forEach((cells, i) => {
    const sellerCustomerId = cells[index.seller] ?? '';
    const tagName = cells[index.tag] ?? '';
    if (!sellerCustomerId && !tagName) return;
    rows.push({
      // ヘッダ行があるぶんを足して、画面に出す行番号を実ファイルに合わせる
      lineNumber: i + (headerIndex ? 2 : 1),
      sellerCustomerId,
      tagName: normalizeDisplayName(tagName),
    });
  });

  return { rows };
}

/** ヘッダ行の列名から列位置を探す。見つからなければ null（＝ヘッダ行ではない） */
function findColumnIndexes(header: string[]): { seller: number; tag: number } | null {
  const seller = header.findIndex((h) => h === '店舗ID' || h === '店舗コード');
  const tag = header.findIndex((h) => h === 'タグ名' || h === 'タグ値');
  if (seller === -1 || tag === -1) return null;
  return { seller, tag };
}

/** ヘッダ行が無いときに列数から位置を決める */
function fallbackColumnIndexes(columnCount: number): { seller: number; tag: number } | null {
  // 雛形と同じ4列（店舗ID, 階層, 店舗名, タグ名）
  if (columnCount >= 4) return { seller: 0, tag: 3 };
  // 手打ちの2列（店舗ID, タグ名）
  if (columnCount === 2) return { seller: 0, tag: 1 };
  return null;
}

/** 登録で何が起きるかを組み立てる */
export function buildImportPlan({
  rows,
  sellers,
  existingTags,
  currentAssignments,
  duplicateResolution = 'last',
  skipNewTagNames = false,
  mergeInto,
}: BuildImportPlanInput): ImportPlan {
  // 内部IDを持たない店舗にはタグを付けられないため、索引から外す
  // （空文字をキーにすると、全店舗が1件に潰れて誤った付与になる）
  const sellerById = new Map(sellers.filter((s) => s.sellerId).map((s) => [s.sellerCustomerId, s]));
  const valueByKey = new Map(existingTags.map((v) => [strictKey(v.name), v]));
  const valueIdToName = new Map(existingTags.map((v) => [v.id, v.name]));
  const existingNames = existingTags.map((v) => v.name);

  // マスタに無い店舗IDは登録しない（黙って捨てず、画面に出すために覚えておく）
  const unknownSellerIds: ImportPlan['unknownSellerIds'] = [];
  const knownRows: ParsedRow[] = [];
  for (const row of rows) {
    if (!row.sellerCustomerId) continue;
    if (!sellerById.has(row.sellerCustomerId)) {
      unknownSellerIds.push({
        lineNumber: row.lineNumber,
        sellerCustomerId: row.sellerCustomerId,
      });
      continue;
    }
    knownRows.push(row);
  }

  // 1店舗にはタグ種別ごとに1件しか付かないので、同じ店舗が複数行あると決められない
  const rowsBySeller = new Map<string, ParsedRow[]>();
  for (const row of knownRows) {
    const list = rowsBySeller.get(row.sellerCustomerId) ?? [];
    list.push(row);
    rowsBySeller.set(row.sellerCustomerId, list);
  }

  const duplicateSellers: DuplicateSeller[] = [];
  const resolvedRows: ParsedRow[] = [];
  for (const [sellerCustomerId, list] of rowsBySeller) {
    // 同じタグ名が並んでいるだけなら迷う余地がないので重複として扱わない
    const distinct = new Set(list.map((r) => strictKey(r.tagName)));
    if (list.length > 1 && distinct.size > 1) {
      duplicateSellers.push({
        sellerCustomerId,
        sellerName: sellerById.get(sellerCustomerId)?.sellerName ?? '',
        rows: list,
      });
      if (duplicateResolution === 'skip') continue;
      resolvedRows.push(duplicateResolution === 'first' ? list[0] : list[list.length - 1]);
      continue;
    }
    resolvedRows.push(list[list.length - 1]);
  }

  // タグ名が空の行は「タグを外す」ではなく「指定なし」として無視する。
  // 外すのは洗い替えか、画面からの明示的な操作に限る
  const withTagName = resolvedRows.filter((r) => r.tagName.length > 0);

  const newTagNames: NewTagName[] = [];
  const seenNewKeys = new Set<string>();
  const entries: ImportPlan['entries'] = [];
  let newlyAssigned = 0;
  let replaced = 0;
  let unchanged = 0;

  for (const row of withTagName) {
    const key = strictKey(row.tagName);
    const merged = mergeInto?.get(key);
    const effectiveName = merged ?? row.tagName;
    const effectiveKey = strictKey(effectiveName);
    const existing = valueByKey.get(effectiveKey);

    if (!existing) {
      if (skipNewTagNames) continue;
      if (!seenNewKeys.has(effectiveKey)) {
        seenNewKeys.add(effectiveKey);
        const check = checkTagName(effectiveName, existingNames);
        newTagNames.push({
          name: effectiveName,
          similarTo:
            check.kind === 'near_duplicate' || check.kind === 'similar'
              ? check.existing
              : undefined,
        });
      }
    }

    const sellerId = sellerById.get(row.sellerCustomerId)?.sellerId ?? '';
    const currentValueId = currentAssignments.get(sellerId);
    if (!currentValueId) {
      newlyAssigned++;
    } else if (strictKey(valueIdToName.get(currentValueId) ?? '') === effectiveKey) {
      unchanged++;
    } else {
      replaced++;
    }

    entries.push({ sellerId, tagName: effectiveName });
  }

  // 洗い替えを選ぶと、CSVに出てこない店舗のタグが外れる
  const sellersInCsv = new Set(entries.map((e) => e.sellerId));
  let unassignedIfReplace = 0;
  for (const sellerId of currentAssignments.keys()) {
    if (!sellersInCsv.has(sellerId)) unassignedIfReplace++;
  }

  return {
    newTagNames,
    duplicateSellers,
    unknownSellerIds,
    newlyAssigned,
    replaced,
    unchanged,
    unassignedIfReplace,
    entries,
  };
}

// ---- 複数タグ種別をまとめて登録する（ワイド形式）----
//
// 店舗階層抽出タブのCSV（タグ種別が列に並ぶ）をそのまま貼り戻せるようにする。
// タグ種別が5つあるとき1種別ずつ5回登録するのは現実的でないため。

/** ワイド形式の差分計算に渡す、既存のタグ種別 */
export interface ImportTagType {
  id: string;
  name: string;
  tags: ImportTag[];
}

/** ワイド形式の1列ぶん。ヘッダのタグ種別名と、その列の値 */
export interface WideColumn {
  /** CSVヘッダに書かれていたタグ種別名 */
  tagTypeName: string;
  /** 既存のタグ種別ID。CSVにしか無い種別（新規作成の対象）では undefined */
  tagTypeId?: string;
  rows: ParsedRow[];
}

/** ワイド形式のパース結果 */
export interface ParsedWide {
  columns: WideColumn[];
  error?: string;
}

/**
 * タグ種別の列として扱わない固定列。
 *
 * タグ種別名がここと衝突すると、CSVの固定列を上書きしたり、取り込み時に
 * 固定列として読み飛ばされたりする。`buildCsvHeaderNames` で接尾辞を付けて避ける。
 */
export const FIXED_COLUMNS = [
  '店舗ID',
  '店舗コード',
  '店舗名',
  '店舗階層',
  '郵便番号',
  '階層',
  'タグ名',
  'タグ値',
];

/**
 * タグ種別名をCSVのヘッダ名に変換する（固定列と衝突しないようにする）。
 *
 * 出力と取り込みで同じ規則を使う必要があるため、ここに1つだけ置く。
 * 衝突した場合は「（タグ）」を付けて逃がす（例: 店舗名 → 店舗名（タグ））。
 */
export function buildCsvHeaderNames(tagTypeNames: string[]): string[] {
  // 判定は strictKey で行う。取り込み側が strictKey で引くため、完全一致で
  // 逃がすと「店舗 ID」のような種別が素通りし、本物の「店舗ID」列に当たってしまう
  const used = new Set<string>(FIXED_COLUMNS.map(strictKey));
  return tagTypeNames.map((name) => {
    let header = name;
    while (used.has(strictKey(header))) {
      header = `${header}（タグ）`;
    }
    used.add(strictKey(header));
    return header;
  });
}

/** 固定列かどうか。種別名と同じ正規化キーで比べる */
function isFixedColumn(name: string): boolean {
  return FIXED_COLUMNS.some((fixed) => strictKey(fixed) === strictKey(name));
}

/**
 * ワイド形式かどうかを判定する。
 *
 * 「タグ名」列があれば従来の1種別形式、無ければワイド形式とみなす。
 * 利用者に形式を選ばせず、貼り付けたものから判別する。
 */
export function isWideFormat(text: string): boolean {
  const parsed = Papa.parse<string[]>(text.trim(), { skipEmptyLines: 'greedy', preview: 1 });
  const header = (parsed.data[0] ?? []).map((c) => (c ?? '').trim());
  if (header.length === 0) return false;
  const hasSeller = header.some((h) => h === '店舗ID' || h === '店舗コード');
  const hasTagColumn = header.some((h) => h === 'タグ名' || h === 'タグ値');
  // 固定列を除いて1列でも残ればワイド形式
  return hasSeller && !hasTagColumn && header.some((h) => h && !FIXED_COLUMNS.includes(h));
}

/**
 * ワイド形式のCSVを、タグ種別ごとの行に割る。
 *
 * ヘッダのタグ種別名は strictKey で既存と突き合わせる（表記揺れを吸収する）。
 * 既存に無い種別名は tagTypeId を持たない列として返し、呼び出し側が
 * 「新規作成するか」「エラーにするか」を決める。
 */
export function parseWideImportText(text: string, existingTypes: ImportTagType[]): ParsedWide {
  const parsed = Papa.parse<string[]>(text.trim(), { skipEmptyLines: 'greedy' });
  if (parsed.data.length === 0) return { columns: [], error: '登録する行がありません' };

  const table = parsed.data.map((row) => row.map((cell) => (cell ?? '').trim()));
  const header = table[0];
  const sellerIndex = header.findIndex((h) => h === '店舗ID' || h === '店舗コード');
  if (sellerIndex === -1) {
    return { columns: [], error: '「店舗ID」列が見つかりません' };
  }

  // ヘッダ名は出力時と同じ規則で引く。衝突回避で接尾辞が付いた列（例: 店舗名（タグ））も
  // 元の種別に戻せるようにするため。衝突しない種別名はそのまま引ける。
  // 種別名そのもの（例: 店舗名）では引かない。固定列と区別できないため
  const headerNames = buildCsvHeaderNames(existingTypes.map((t) => t.name));
  const typeByHeader = new Map(
    existingTypes.map((t, i) => [strictKey(headerNames[i]), t] as const),
  );
  const columns: WideColumn[] = [];

  header.forEach((name, colIndex) => {
    if (!name) return;
    const displayName = normalizeDisplayName(name);
    const existing = typeByHeader.get(strictKey(displayName));
    if (!existing && isFixedColumn(name)) return;
    const rows: ParsedRow[] = [];
    table.slice(1).forEach((cells, i) => {
      const sellerCustomerId = cells[sellerIndex] ?? '';
      if (!sellerCustomerId) return;
      rows.push({
        // ヘッダ行があるぶんを足して、画面に出す行番号を実ファイルに合わせる
        lineNumber: i + 2,
        sellerCustomerId,
        tagName: normalizeDisplayName(cells[colIndex] ?? ''),
      });
    });
    columns.push({
      // 既存に当たったものは、CSVのヘッダ名ではなく登録されている種別名を使う
      tagTypeName: existing?.name ?? displayName,
      tagTypeId: existing?.id,
      rows,
    });
  });

  if (columns.length === 0) {
    return { columns: [], error: 'タグ種別の列が見つかりません（固定列しかありません）' };
  }
  return { columns };
}

/** タグ種別1つぶんの登録内容 */
export interface WidePlanEntry {
  tagTypeName: string;
  /** 既存の種別ID。新規作成する種別では undefined */
  tagTypeId?: string;
  plan: ImportPlan;
}

/** ワイド形式全体の登録内容 */
export interface WideImportPlan {
  entries: WidePlanEntry[];
  /** CSVにしか無いタグ種別名（新規作成の対象） */
  newTagTypeNames: string[];
}

/**
 * タグ種別ごとに buildImportPlan を回し、結果を束ねる。
 *
 * 1種別ぶんの差分計算は既存のものをそのまま使う
 * （重複・マスタ照合・件数の数え方を一箇所に保つため）。
 */
export function buildWideImportPlan({
  columns,
  sellers,
  existingTypes,
  assignmentsByType,
  duplicateResolution = 'last',
}: {
  columns: WideColumn[];
  sellers: ImportSeller[];
  existingTypes: ImportTagType[];
  /** タグ種別ID → (店舗の内部ID → タグID) */
  assignmentsByType: Map<string, Map<string, string>>;
  duplicateResolution?: DuplicateResolution;
}): WideImportPlan {
  const tagsByTypeId = new Map(existingTypes.map((t) => [t.id, t.tags]));
  const entries: WidePlanEntry[] = [];
  const newTagTypeNames: string[] = [];

  for (const column of columns) {
    if (!column.tagTypeId) newTagTypeNames.push(column.tagTypeName);
    entries.push({
      tagTypeName: column.tagTypeName,
      tagTypeId: column.tagTypeId,
      plan: buildImportPlan({
        rows: column.rows,
        sellers,
        // 新規作成する種別には既存のタグも付与も無い
        existingTags: column.tagTypeId ? (tagsByTypeId.get(column.tagTypeId) ?? []) : [],
        currentAssignments: column.tagTypeId
          ? (assignmentsByType.get(column.tagTypeId) ?? new Map<string, string>())
          : new Map<string, string>(),
        duplicateResolution,
      }),
    });
  }
  return { entries, newTagTypeNames };
}
